import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "../services/api";
import {
  acceptAuthenticatedUserSession,
  clearCsrfToken,
  getAuthGeneration,
  hasCsrfToken,
  invalidateBrowserSession,
  logoutBrowserSession,
  refreshBrowserSession,
  requireReauthentication,
  setAuthorizationChangeHandler,
  setReauthenticationHandler,
} from "../services/browserAuth";
import { purgeProtectedQueryCache } from "../queryClient";

const AUTH_STORAGE_VERSION = 1;
let identityRefreshOperation = null;

function userIdentity(user) {
  return user?.id === undefined || user?.id === null ? null : String(user.id);
}

export function sanitizePersistedAuthState(state) {
  const user =
    state?.user && typeof state.user === "object" && !Array.isArray(state.user)
      ? state.user
      : null;

  return {
    isAuthenticated: state?.isAuthenticated === true && user !== null,
    user,
  };
}

/**
 * @typedef {Object} Role
 * @property {number} id
 * @property {string} name
 * @property {string} description
 */

/**
 * @typedef {Object} User
 * @property {number} id
 * @property {string} name
 * @property {string} email
 * @property {Role[]} roles
 * @property {string} created_at
 * @property {string} updated_at
 */

/**
 * @typedef {Object} AuthState
 * @property {boolean} isAuthenticated
 * @property {User|null} user
 * @property {boolean} freshAuthVerified
 * @property {boolean} isLoading
 * @property {string|null} error
 * @property {Function} login
 * @property {Function} logout
 * @property {Function} refresh
 * @property {Function} setLoading
 * @property {Function} setError
 * @property {Function} clearError
 */

export const useAuthStore = create(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      user: null,
      freshAuthVerified: false,
      isLoading: false,
      error: null,

      setUser: (userData) => {
        if (userData) {
          purgeProtectedQueryCache();
          acceptAuthenticatedUserSession();
        }

        set({
          user: userData,
          isAuthenticated: !!userData,
          freshAuthVerified: false,
        });
      },

      setFreshUser: (userData) => {
        if (
          userData &&
          userIdentity(get().user) !== userIdentity(userData)
        ) {
          purgeProtectedQueryCache();
          acceptAuthenticatedUserSession();
        }

        set({
          user: userData,
          isAuthenticated: !!userData,
          freshAuthVerified: true,
        });
      },

      clearSession: ({ authInvalidated = false } = {}) => {
        purgeProtectedQueryCache();
        if (!authInvalidated) invalidateBrowserSession();
        clearCsrfToken();
        set({
          isAuthenticated: false,
          user: null,
          freshAuthVerified: false,
          error: null,
        });
        localStorage.removeItem("auth-storage");
      },

      refresh: async () => {
        try {
          await refreshBrowserSession();

          return { success: true };
        } catch (error) {
          requireReauthentication(
            error?.code === "csrf_token_missing"
              ? "csrf_token_missing"
              : "session_changed"
          );
          throw error;
        }
      },

      canManageUsers: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return roleNames.includes("superadmin") || roleNames.includes("admin");
      },

      canManageContent: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return ["superadmin", "admin", "editor"].some((r) =>
          roleNames.includes(r)
        );
      },

      isSuperAdmin: () => {
        const { user } = get();
        if (!user) return false;
        return user.roles?.some((r) => r.name === "superadmin");
      },

      isAdmin: () => {
        const { user } = get();
        if (!user) return false;
        const roleNames = user.roles?.map((r) => r.name) || [];
        return roleNames.includes("superadmin") || roleNames.includes("admin");
      },

      logout: async () => {
        let outcome = { success: true, serverRevoked: true };

        purgeProtectedQueryCache();
        set({ freshAuthVerified: false });

        try {
          await logoutBrowserSession();
        } catch {
          outcome = {
            success: false,
            serverRevoked: false,
            error:
              "Local session cleared, but server session revocation could not be confirmed.",
          };
        }

        get().clearSession({ authInvalidated: true });
        if (!outcome.success) set({ error: outcome.error });

        return outcome;
      },

      refreshUser: () => {
        const generation = getAuthGeneration();
        if (identityRefreshOperation?.generation === generation) {
          return identityRefreshOperation.promise;
        }

        set({ freshAuthVerified: false });
        const promise = (async () => {
          try {
            const response = await api.get("/auth/me");
            if (generation !== getAuthGeneration()) {
              return { success: false };
            }

            if (response.data.success && response.data.data) {
              if (!hasCsrfToken()) {
                requireReauthentication("csrf_token_missing");
                return { success: false };
              }

              get().setFreshUser(response.data.data);
              return { success: true };
            }

            get().clearSession();
          } catch (error) {
            if (
              error?.code === "stale_response" ||
              generation !== getAuthGeneration()
            ) {
              return { success: false };
            }

            const status = error?.response?.status ?? error?.status;
            const errorCode = error?.response?.data?.error ?? error?.code;

            if (status === 403 && errorCode === "account_inactive") {
              requireReauthentication("account_inactive");
            } else if (status === 401 || status === 419) {
              requireReauthentication(errorCode || "session_changed");
            } else {
              get().clearSession();
            }
          }

          return { success: false };
        })();
        const operation = { generation, promise };
        identityRefreshOperation = operation;
        promise.then(
          () => {
            if (identityRefreshOperation === operation) {
              identityRefreshOperation = null;
            }
          },
          () => {
            if (identityRefreshOperation === operation) {
              identityRefreshOperation = null;
            }
          }
        );

        return promise;
      },

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: "auth-storage",
      version: AUTH_STORAGE_VERSION,
      migrate: (persistedState) => sanitizePersistedAuthState(persistedState),
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...sanitizePersistedAuthState(persistedState),
        freshAuthVerified: false,
      }),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);

const reauthenticationMessages = {
  account_inactive: "Account is inactive. Contact an administrator.",
  csrf_failed: "Browser session is stale. Sign in again.",
  csrf_token_missing: "Browser session requires sign-in after page reload.",
  refresh_blocked: "Browser session changed. Sign in again.",
  session_changed: "Authentication session changed. Sign in again.",
  session_expired: "Session expired or was revoked. Sign in again.",
};

setReauthenticationHandler((reason) => {
  useAuthStore.getState().clearSession({ authInvalidated: true });
  useAuthStore.setState({
    error:
      reauthenticationMessages[reason] || reauthenticationMessages.session_expired,
  });
});

setAuthorizationChangeHandler(() => {
  purgeProtectedQueryCache();
  if (useAuthStore.getState().freshAuthVerified === true) {
    acceptAuthenticatedUserSession();
  }
  useAuthStore.setState({ freshAuthVerified: false });
  queueMicrotask(() => {
    void useAuthStore.getState().refreshUser();
  });
});
